import { z } from 'zod';
/**
 * Thanh điều hướng trên cùng của **một người, trong một tổ chức** — APM-NAV-b.
 *
 * Mỗi mục điều hướng nằm ở đúng một trong ba chỗ: **thanh** (tối đa
 * `NAV_BAR_MAX` mục, hiện thẳng ra), **menu "Tính năng"**, hoặc **ẩn**. Lưu ở
 * `org_membership.nav_layout`; `null` = chưa sắp gì, dùng bố cục mặc định.
 *
 * ## Vì sao id là chuỗi tự do, không phải enum
 *
 * Danh mục mục điều hướng sống ở web (icon, quyền, đường dẫn) và sẽ đổi: thêm
 * trang, bỏ trang. Một enum ở đây thì một hàng cũ mang id đã bỏ làm server từ
 * chối cả bố cục, còn một trình duyệt mở từ trước lúc deploy gửi id mới thì
 * nhận 400. Phía đọc (`resolveNavLayout`) lọc theo danh mục thật, nên id lạ
 * chỉ là một chữ thừa trong cột JSON.
 *
 * ## Vì sao lưu cả `hidden`, không suy ra từ "không có trong bar/menu"
 *
 * Mục **mới** (trang vừa thêm, hay quyền vừa được cấp) không có trong bố cục
 * đã lưu. Suy "vắng mặt = ẩn" thì nó không hiện ở đâu cả và không gì báo cho
 * người dùng biết là có — thứ hỏng im lặng. Vắng mặt nghĩa là "chưa sắp", và
 * mục ấy về chỗ mặc định của nó. Cùng lý lẽ với `sidebar_layout.hidden`.
 */
export const NAV_BAR_MAX = 5;
const NavItemId = z.string().trim().min(1).max(40);
export const NavLayout = z.object({
    bar: z.array(NavItemId).max(NAV_BAR_MAX),
    menu: z.array(NavItemId).max(50),
    hidden: z.array(NavItemId).max(50),
});
/** `null` = về bố cục mặc định. Bọc trong object: `null` trần thành body rỗng (bẫy ở CLAUDE.md). */
export const NavLayoutView = z.object({ layout: NavLayout.nullable() });
export const UpdateNavLayoutInput = NavLayoutView;
/**
 * Bố cục thật sẽ vẽ, từ bố cục đã lưu và danh mục mục **người này được thấy**.
 *
 * - `allowed`: mọi mục được thấy, theo thứ tự mặc định (thanh trước, menu sau).
 * - `defaultBar`: những mục mặc định nằm trên thanh.
 *
 * Mục đã lưu mà nay không được thấy (mất quyền, trang đã bỏ) thì biến mất, và
 * **không** có mục nào tự nhảy lên lấp chỗ trống trên thanh: người dùng đã
 * chọn thanh của họ. Mục chưa sắp về chỗ mặc định; thanh đã đủ thì vào menu.
 * Một id có ở nhiều chỗ thì chỗ đứng trước thắng (thanh → menu → ẩn).
 */
export function resolveNavLayout(layout, allowed, defaultBar) {
    const known = new Set(allowed);
    const inBar = new Set(defaultBar);
    if (!layout) {
        const bar = allowed.filter((id) => inBar.has(id)).slice(0, NAV_BAR_MAX);
        const onBar = new Set(bar);
        return { bar, menu: allowed.filter((id) => !onBar.has(id)), hidden: [] };
    }
    const placed = new Set();
    const take = (ids) => ids.filter((id) => {
        if (!known.has(id) || placed.has(id))
            return false;
        placed.add(id);
        return true;
    });
    const onBar = take(layout.bar);
    const bar = onBar.slice(0, NAV_BAR_MAX);
    // Phần tràn của thanh (hàng ghi tay, hay trần giảm) không được mất: nó về đầu menu.
    const menu = [...onBar.slice(NAV_BAR_MAX), ...take(layout.menu)];
    const hidden = take(layout.hidden);
    for (const id of allowed) {
        if (placed.has(id))
            continue;
        if (inBar.has(id) && bar.length < NAV_BAR_MAX)
            bar.push(id);
        else
            menu.push(id);
    }
    return { bar, menu, hidden };
}
//# sourceMappingURL=nav-layout.js.map